import { forwardRef, useCallback, useEffect } from 'react';

import debounce from 'lodash/debounce';

import { getCards } from 'api/Cards';

import { iaRa } from 'lib/helpers/utility';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';

import { PAIRED_CARD_FIELD_ROWS } from 'components/Payments/payroc/lib/cardFieldRows';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

import NewCard from '../../PayWithCardModal/NewCard';
import { useReportFormLoadError } from '../../PaymentEntryModal/lib/formLoadErrorContext';

import SavedCards from './SavedCards';

const SCROLL_THRESHOLD = 30;

const CardContent = forwardRef(({ active, setActive, patientId, setErrorMessage }, ref) => {
  const { values, terminalId, processor } = usePaymentContext();
  const reportLoadError = useReportFormLoadError();

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteFetchData({
    queryKey: 'cards',
    queryFn: (navigate, params) => getCards(navigate, params),
    formatData: (data) => iaRa(data).filter((card) => !!card?.id),
    params: { patientId, terminalId, withCount: true },
    dependencies: [terminalId, patientId],
    options: {
      // Saved cards are temporarily hidden on the kiosk (see render below), so don't fetch them.
      enabled: false
    }
  });

  const handleScroll = useCallback(() => {
    const div = ref.current;
    if (!div) return;

    const isNearBottom = div.scrollTop + div.clientHeight >= div.scrollHeight - SCROLL_THRESHOLD;

    if (isNearBottom && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const div = ref.current;
    if (!div) return;

    const debounced = debounce(handleScroll, 200);
    div.addEventListener('scroll', debounced);

    return () => {
      div.removeEventListener('scroll', debounced);
      debounced.cancel();
    };
  }, [handleScroll, active]);

  return (
    <>
      {active === 'saved-cards' && (
        // Saved cards are temporarily hidden on the kiosk: charging a saved card here fails
        // (cause still under investigation), so we don't offer them. Tap/insert on the reader
        // and "Enter card manually" still work. Restore `cards={cards}` once the charge is fixed.
        <SavedCards cards={[]} active={values.methodDetail} setActive={setActive} />
      )}
      {active === 'credit-card' && processor === 'payroc' && (
        <NewCard
          onError={(error) => setErrorMessage(error)}
          fieldRows={PAIRED_CARD_FIELD_ROWS}
          hideButton
          onLoadError={reportLoadError}
        />
      )}
    </>
  );
});

export default CardContent;
